// 一覧や表の並び替えを配列操作として純粋関数に切り出す。
// 呼び出し元の描画コードから並び替えロジックを分離するため。

export type MoveDirection = 'up' | 'down';

function neighborIndexOf(index: number, direction: MoveDirection): number {
  if (direction === 'up') {
    return index - 1;
  }
  return index + 1;
}

export function swapWithNeighbor<T>(items: T[], index: number, direction: MoveDirection): T[] {
  const neighborIndex = neighborIndexOf(index, direction);
  const isValidSwap = neighborIndex >= 0 && neighborIndex < items.length;
  if (isValidSwap) {
    const result = items.slice();
    const temp = result[index];
    result[index] = result[neighborIndex];
    result[neighborIndex] = temp;
    return result;
  }
  return items.slice();
}

export function moveItem<T>(items: T[], sourceIndex: number, targetIndex: number): T[] {
  const isInRange = sourceIndex >= 0 && sourceIndex < items.length && targetIndex >= 0 && targetIndex < items.length;
  if (isInRange) {
    const result = items.slice();
    const removed = result.splice(sourceIndex, 1);
    const movedItem = removed[0];
    result.splice(targetIndex, 0, movedItem);
    return result;
  }
  return items.slice();
}

/** 2つの並び順が同じかどうか */
export function isSameOrder<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, index) => item === b[index]);
}

/**
 * 一覧に出す並び順。画面の中で動かした並び（draftIds）があればそれを使い、
 * その間に増えたり消えたりした行があれば、サーバーの並びに合わせて足し引きする
 */
export function getDisplayedIds(serverIds: number[], draftIds: number[] | null): number[] {
  if (draftIds === null) {
    return serverIds;
  }
  const kept = draftIds.filter((id) => serverIds.indexOf(id) !== -1);
  const added = serverIds.filter((id) => kept.indexOf(id) === -1);
  return [...kept, ...added];
}

/**
 * id の並び順に合わせて、行のデータを並べ直す。並び順にあってデータにない id は飛ばす。
 * 一覧の行を、画面の中で動かした並びで出すために使う
 */
export function orderByIds<T extends { id: number }>(items: T[], ids: number[]): T[] {
  const result: T[] = [];
  ids.forEach((id) => {
    const item = items.find((candidate) => candidate.id === id);
    if (item !== undefined) {
      result.push(item);
    }
  });
  return result;
}
